import "i18next";
import { useTranslation } from "react-i18next";
import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";
import {
  getFileAttachment,
  saveFileAttachment,
  deleteFileAttachment,
  getFileAttachmentDetails,
} from "../../services/userServices";
import ExcelJS from "exceljs";
import PrimeReactDataGrid from "components/PrimeReactDataGrid/PrimeReactDataGrid";
import { useDropzone } from "react-dropzone";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { Close } from "@mui/icons-material";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Slide,
  Typography,
  Button,
  Paper,
  Stack,
  Box,
  Chip,
  Autocomplete,
  TextField,
  Tooltip,
  ListItem,
  ListItemText,
  Portal,
  Link,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import OpenInBrowserIcon from "@mui/icons-material/OpenInBrowser";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DialogBox from "components/ConfirmationDialog/DialogBox";
const CustomTypography = styled(Typography)({
  fontFamily: "Urbanist, sans-serif",
});
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});
const AutocompleteWrapper = styled("div")({
  maxHeight: "100px",
  overflowY: "auto",
});
const MainHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: 18,
  fontWeight: 800,
  letterSpacing: "0.10em",
  paddingBottom: theme.spacing(1),
}));
const SubHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "Urbanist, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  fontWeight: "bold",
  paddingBottom: theme.spacing(0.75),
}));
const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));
export default function FileAttachment({
  attachmentMode,
  setLoading,
  setSnack,
  classRequestId,
  classRosterId,
  attachmentDialogOpen,
  handleAttachmentDialogClose,
}) {
  const { t } = useTranslation(["FileAttachment"], {
    useSuspense: false,
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [attachmentTypes, setAttachmentTypes] = useState([]);
  const [fileAttachmentDetails, setFileAttachmentDetails] = useState([]);
  const [filteredAttachmentDetails, setFilteredAttachmentDetails] = useState(
    [],
  );
  const [productionScheduleDetails, setProductionScheduleDetails] = useState(
    [],
  );
  const [selectedAttachmentType, setSelectedAttachmentType] = useState(0);
  const [showDropzone, setShowDropzone] = useState(false);
  const allowedExtensions = useMemo(
    () => [
      ".csv",
      ".docx",
      ".msg",
      ".pdf",
      ".txt",
      ".xls",
      ".xlsb",
      ".xlsm",
      ".xlsx",
      ".zip",
    ],
    [],
  );
  const [dropzoneError, setDropzoneError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogConfirmAction, setDialogConfirmAction] = useState(null);
  const [dialogCancelAction, setDialogCancelAction] = useState(null);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogMessageType, setDialogMessageType] = useState("Confirmation");
  const [dialogBulletList, setDialogBulletList] = useState([]);
  const [isConfirmationDialog, setIsConfirmationDialog] = useState(true);
  const MAX_FILE_SIZE_MB = 10;
  const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
  const MAX_FILE_ALLOWED = 5;
  const handleDialogOpen = (
    action,
    cancelAction,
    title,
    message,
    messageType,
    dialogBulletList,
    isConfirmation,
  ) => {
    setDialogConfirmAction(() => action);
    if (cancelAction) {
      setDialogCancelAction(() => cancelAction);
    }
    setDialogTitle(title);
    setDialogMessage(message);
    setDialogMessageType(messageType);
    setDialogBulletList(dialogBulletList);
    setIsConfirmationDialog(isConfirmation);
    setDialogOpen(true);
  };
  const handleDeleteRosterAttachment = async (event, value) => {
    const deleteAttachmentDialog = {
      title: t("delete_attachment.title"),
      message: t("delete_attachment.message"),
      messageType: "Confirmation",
      details: [],
      isConfirmation: true,
    };
    handleDialogOpen(
      async () => {
        try {
          setLoading(true);
          const deleteFile = await deleteFileAttachment(
            value.fileDocLibID,
            localStorage.getItem("User"),
          );
          if (deleteFile.status === true) {
            const updatedFileAttachmentDetails = fileAttachmentDetails.filter(
              (item) => item.fileDocLibID !== value.fileDocLibID,
            );
            setFileAttachmentDetails(updatedFileAttachmentDetails);
            setFilteredAttachmentDetails(
              selectedAttachmentType !== 0
                ? updatedFileAttachmentDetails.filter(
                    (item) =>
                      item.fileAttachmentTypeId === value.fileAttachmentTypeId,
                  )
                : updatedFileAttachmentDetails,
            );
            const fileCounts = updatedFileAttachmentDetails.reduce(
              (acc, item) => {
                acc[item.fileAttachmentTypeId] =
                  (acc[item.fileAttachmentTypeId] || 0) + 1;
                return acc;
              },
              {},
            );
            const updatedAttachmentTypes = attachmentTypes.map((type) => ({
              ...type,
              totalFiles: fileCounts[type.fileAttachmentTypeId] || 0,
            }));
            setAttachmentTypes(updatedAttachmentTypes);
            setSnack({
              open: true,
              message: t(
                "alert.requested_file_attachment_has_been_successfully_deleted_",
              ),
              severity: "success",
            });
          } else {
            setSnack({
              open: true,
              message: t(
                "alert.error_occurred_the_file_may_have_already_been_deleted_please_refresh_to_check_",
              ),
              severity: "error",
            });
          }
        } catch {
          setSnack({
            open: true,
            message: t("alert.error_while_deleting_attachment_"),
            severity: "error",
          });
        } finally {
          setLoading(false);
        }
      },
      null,
      deleteAttachmentDialog.title,
      deleteAttachmentDialog.message,
      deleteAttachmentDialog.messageType,
      deleteAttachmentDialog.details,
      deleteAttachmentDialog.isConfirmation,
    );
  };
  const fileAttachmentColumns = [
    {
      field: "fileDocLibID",
      header: t("column_header.delete"),
      align: "center",
      bodyStyle: {
        textAlign: "center",
      },
      body: (rowData) => (
        <Tooltip
          title={
            <Typography
              variant="body2"
              sx={{
                fontSize: "1rem",
              }}
            >
              {rowData.totalUsage > 0
                ? t("typography.cannot_delete_file_is_in_use")
                : t("typography.delete_this_file")}
            </Typography>
          }
          arrow
        >
          <IconButton
            onClick={
              rowData.totalUsage > 0
                ? null
                : (event) => handleDeleteRosterAttachment(event, rowData)
            }
            sx={{
              color: rowData.totalUsage > 0 ? "grey.500" : "#3299FF",
              fontSize: "1.75rem",
              cursor: rowData.totalUsage > 0 ? "not-allowed" : "pointer",
            }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "fileName",
      header: t("column_header.file_name"),
      sortable: true,
      align: "center",
      bodyStyle: {
        textAlign: "left",
      },
      body: (rowData) => (
        <Link
          href={rowData.fileURLPath}
          target="_blank"
          rel="noopener noreferrer"
          title={rowData.fileName}
          sx={{
            color: "#3299FF",
            textDecoration: "none",
            display: "inline-block",
            maxWidth: "200px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {rowData.fileName}
        </Link>
      ),
    },
    {
      field: "fileAttachmentType",
      header: t("column_header.attachment_type"),
      sortable: true,
      align: "center",
      bodyStyle: {
        textAlign: "left",
      },
    },
    {
      field: "modifiedBy",
      header: t("column_header.modified_by"),
      sortable: true,
      align: "center",
      bodyStyle: {
        textAlign: "left",
      },
    },
    {
      field: "modifiedDate",
      header: t("column_header.modified_date"),
      sortable: true,
      align: "center",
      bodyStyle: {
        textAlign: "center",
      },
      body: (rowData) =>
        rowData.modifiedDate
          ? new Date(rowData.modifiedDate).toLocaleString()
          : "NA",
    },
  ];
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!cancelled) {
        setLoading(true);
      }

      try {
        if (classRequestId) {
          if (!cancelled) {
            setAttachmentTypes([]);
            setFilteredAttachmentDetails([]);
            setFileAttachmentDetails([]);
            setSelectedAttachmentType(0);
            setDropzoneError("");
            setSelectedFiles([]);
            setShowDropzone(false);
          }

          const attachmentTypeResponse =
            await getFileAttachment(attachmentMode);

          if (cancelled) return;

          if (attachmentTypeResponse?.message === "Success") {
            const attachmentTypesData =
              attachmentTypeResponse?.data.map((type) => ({
                ...type,
                totalFiles: 0,
              })) || [];

            if (!cancelled) {
              setAttachmentTypes(attachmentTypesData);
            }

            const lookupUniqueId =
              attachmentMode === "ClassRequest"
                ? classRequestId
                : classRosterId;

            const fileAttachmentDetailsResponse =
              await getFileAttachmentDetails(lookupUniqueId, attachmentMode);

            if (cancelled) return;

            if (fileAttachmentDetailsResponse?.message === "Success") {
              const fileAttachmentDetailsData =
                fileAttachmentDetailsResponse?.data || [];

              const fileCounts = fileAttachmentDetailsData.reduce(
                (acc, item) => {
                  acc[item.fileAttachmentTypeId] =
                    (acc[item.fileAttachmentTypeId] || 0) + 1;
                  return acc;
                },
                {},
              );

              const updatedAttachmentTypes = attachmentTypesData.map(
                (type) => ({
                  ...type,
                  totalFiles: fileCounts[type.fileAttachmentTypeId] || 0,
                }),
              );

              if (!cancelled) {
                setAttachmentTypes(updatedAttachmentTypes);
                setFileAttachmentDetails(fileAttachmentDetailsData);
                setFilteredAttachmentDetails(fileAttachmentDetailsData);
              }
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          setSnack({
            open: true,
            message: t("alert.error_fetching_file_attachment_data"),
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [attachmentMode, classRequestId, classRosterId, setLoading, setSnack]);
  const handleValueChange = async (event, newValue) => {
    try {
      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (!newValue) {
        setSnack({
          open: true,
          message: t("alert.please_select_file_attachment_type_"),
          severity: "error",
        });
        return;
      }
      setFilteredAttachmentDetails(
        newValue.fileAttachmentTypeId === 0
          ? fileAttachmentDetails
          : fileAttachmentDetails.filter(
              (item) =>
                item.fileAttachmentTypeId ===
                Number(newValue.fileAttachmentTypeId),
            ),
      );
      setSelectedAttachmentType(newValue?.fileAttachmentTypeId || 0);
      setDropzoneError("");
      setSelectedFiles([]);
      setShowDropzone(false);
    } catch {
      setSnack({
        open: true,
        message: t("alert.error_saving_file_attachment_data"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  const isFileExtensionAllowed = useCallback(
    (fileName) => {
      const extension = fileName
        .substring(fileName.lastIndexOf("."))
        .toLowerCase();
      return allowedExtensions.includes(extension);
    },
    [allowedExtensions],
  );
  const validateFileContent = useCallback(
    async (file) => {
      const allowedFileTypes = [".xlsx", ".xls"];
      const fileType = file.name.slice(-5).toLowerCase();
      if (!allowedFileTypes.includes(fileType)) {
        setSnack({
          open: true,
          message: t("alert.please_select_a_xlsx_or_xls_file_"),
          severity: "error",
        });
        setSelectedFiles([]);
        return false;
      }

      const reader = new FileReader();
      const processFileContent = async (content) => {
        const expectedHeaders = ["Start Timings", "Days Worked", "Shift Span"];
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(content);
        const worksheet = workbook.worksheets[0];

        // Validate worksheet structure
        const structureValidation = validateWorksheetStructure(
          worksheet,
          expectedHeaders,
        );
        if (!structureValidation.isValid) {
          showErrorFiles(structureValidation.message);
          return false;
        }

        const fileHeaders = worksheet
          .getRow(1)
          .values.filter((value) => value !== null && value !== undefined);

        const indices = {
          shiftTimings: fileHeaders.indexOf("Start Timings") + 1,
          daysWorked: fileHeaders.indexOf("Days Worked") + 1,
          shiftSpan: fileHeaders.indexOf("Shift Span") + 1,
        };

        const allRows = worksheet
          .getRows(2, worksheet.rowCount - 1)
          .map((row) => row.values);

        // Process rows
        const rowValidation = validateAndProcessRows(allRows, indices);
        if (!rowValidation.isValid) {
          showErrorFiles(rowValidation.message);
          return false;
        }

        setProductionScheduleDetails(rowValidation.data);
        return true;
      };

      // Helper function to validate worksheet structure
      const validateWorksheetStructure = (worksheet, expectedHeaders) => {
        if (!worksheet) {
          return {
            isValid: false,
            message: t("alert.no_worksheet_found_in_excel_file_"),
          };
        }

        const fileHeaders = worksheet
          .getRow(1)
          .values.filter((value) => value !== null && value !== undefined);

        if (!expectedHeaders.every((header) => fileHeaders.includes(header))) {
          return {
            isValid: false,
            message: t("alert.invalid_column_headers_"),
          };
        }

        if (worksheet.rowCount <= 1) {
          return {
            isValid: false,
            message: t("alert.no_data_rows_found_in_excel_file_"),
          };
        }

        return { isValid: true };
      };

      // Helper function to validate and process all rows
      const validateAndProcessRows = (allRows, indices) => {
        const productionSchedule = [];
        const shiftTimingsRegex =
          /^([1-9]|1[0-2]):([0-5][0-9])\s?(AM|PM)(?:\s?([A-Z]{2,4}))?\s?-\s?([1-9]|1[0-2]):([0-5][0-9])\s?(AM|PM)(?:\s?([A-Z]{2,4}))?$/i;
        const decimalRegex = /^\d+(\.\d+)?$/;

        for (const row of allRows) {
          const values = {
            shiftTimings: row[indices.shiftTimings],
            daysWorked: row[indices.daysWorked],
            shiftSpan: row[indices.shiftSpan],
          };

          const rowNumber = allRows.indexOf(row) + 2;

          // Validate row data
          const rowValidation = validateRowData(
            values,
            rowNumber,
            shiftTimingsRegex,
            decimalRegex,
          );
          if (!rowValidation.isValid) {
            return rowValidation;
          }

          productionSchedule.push({
            shiftTimings: values.shiftTimings,
            dayWorked: values.daysWorked,
            shiftSpan: values.shiftSpan,
          });
        }

        return { isValid: true, data: productionSchedule };
      };

      // Helper function to validate individual row data
      const validateRowData = (
        values,
        rowNumber,
        shiftTimingsRegex,
        decimalRegex,
      ) => {
        const match = values.shiftTimings
          ? values.shiftTimings.trim().match(shiftTimingsRegex)
          : null;

        if (!match) {
          return {
            isValid: false,
            message: t("attachment_file_error.message01", { rowNumber }),
          };
        }

        const timezoneValidation = validateTimezones(match[4], match[8]);
        if (!timezoneValidation.isValid) {
          return {
            isValid: false,
            message: t("attachment_file_error.message04", { rowNumber }),
          };
        }

        if (!values.daysWorked) {
          return {
            isValid: false,
            message: t("attachment_file_error.message02", { rowNumber }),
          };
        }

        if (!values.shiftSpan || !decimalRegex.test(values.shiftSpan)) {
          return {
            isValid: false,
            message: t("attachment_file_error.message03", { rowNumber }),
          };
        }

        return { isValid: true };
      };

      // Helper function to validate timezones
      const validateTimezones = (tz1, tz2) => {
        const validTimezones = [
          "ACDT",
          "ACST",
          "ACT",
          "ADT",
          "AEDT",
          "AEST",
          "AFT",
          "AKDT",
          "AKST",
          "ALMT",
          "AMST",
          "AMT",
          "ANAST",
          "ANAT",
          "AQTT",
          "ART",
          "AST",
          "AWDT",
          "AWST",
          "AZOST",
          "AZOT",
          "AZST",
          "AZT",
          "BDT",
          "BIOT",
          "BIT",
          "BOT",
          "BRST",
          "BRT",
          "BST",
          "BTT",
          "CAT",
          "CCT",
          "CDT",
          "CEST",
          "CET",
          "CHADT",
          "CHAST",
          "CHOST",
          "CHOT",
          "CHST",
          "CHUT",
          "CIST",
          "CIT",
          "CKT",
          "CLST",
          "CLT",
          "COST",
          "COT",
          "CST",
          "CT",
          "CVT",
          "CWST",
          "CXT",
          "DAVT",
          "DDUT",
          "DFT",
          "EASST",
          "EAST",
          "EAT",
          "ECT",
          "EDT",
          "EEST",
          "EET",
          "EGST",
          "EGT",
          "EST",
          "ET",
          "FET",
          "FJT",
          "FKST",
          "FKT",
          "FNT",
          "GALT",
          "GAMT",
          "GET",
          "GFT",
          "GILT",
          "GMT",
          "GST",
          "GYT",
          "HDT",
          "HAEC",
          "HST",
          "HKT",
          "HMT",
          "HOVT",
          "ICT",
          "IDLW",
          "IDT",
          "IOT",
          "IRDT",
          "IRKT",
          "IRST",
          "IST",
          "JST",
          "KGT",
          "KOST",
          "KRAT",
          "KST",
          "LHST",
          "LINT",
          "MAGT",
          "MART",
          "MAWT",
          "MDT",
          "MET",
          "MEST",
          "MHT",
          "MIST",
          "MIT",
          "MMT",
          "MSK",
          "MST",
          "MT",
          "MYT",
          "NCT",
          "NDT",
          "NFT",
          "NPT",
          "NST",
          "NT",
          "NUT",
          "NZDT",
          "NZST",
          "OMST",
          "ORAT",
          "PDT",
          "PET",
          "PETT",
          "PGT",
          "PHOT",
          "PHT",
          "PKT",
          "PMDT",
          "PMST",
          "PONT",
          "PST",
          "PT",
          "PWT",
          "PYST",
          "PYT",
          "RET",
          "ROTT",
          "SAKT",
          "SAMT",
          "SAST",
          "SBT",
          "SCT",
          "SGT",
          "SLST",
          "SRET",
          "SRT",
          "SST",
          "SYOT",
          "TAHT",
          "THA",
          "TFT",
          "TJT",
          "TKT",
          "TLT",
          "TMT",
          "TRT",
          "TOT",
          "TVT",
          "ULAST",
          "ULAT",
          "UTC",
          "UYST",
          "UYT",
          "UZT",
          "VET",
          "VLAT",
          "VOLT",
          "VOST",
          "VUT",
          "WAKT",
          "WAST",
          "WAT",
          "WEST",
          "WET",
          "WIB",
          "WIT",
          "WITA",
          "WST",
          "YAKT",
          "YEKT",
        ];

        const isTimezoneValid =
          (!tz1 && !tz2) ||
          (tz1 &&
            tz2 &&
            validTimezones.includes(tz1.toUpperCase()) &&
            tz1.toUpperCase() === tz2.toUpperCase()) ||
          (!tz1 && tz2 && validTimezones.includes(tz2.toUpperCase()));

        return { isValid: isTimezoneValid };
      };

      // Helper function to show error and reset state
      const showErrorFiles = (message) => {
        setSnack({
          open: true,
          message,
          severity: "error",
        });
        setSelectedFiles([]);
      };

      reader.onload = async (event) => processFileContent(event.target.result);
      reader.onerror = async () => {
        setSnack({
          open: true,
          message: t("alert.error_reading_file_"),
          severity: "error",
        });
        setSelectedFiles([]);
      };

      reader.readAsArrayBuffer(file);
      return true;
    },
    [setSnack, setSelectedFiles, setProductionScheduleDetails],
  );

  const validateAndUploadFiles = useCallback(
    async (files) => {
      // Early validation for attachment type 6
      const typeValidation = validateAttachmentTypeConstraints(files);
      if (!typeValidation.isValid) {
        showErrorDropzone(typeValidation.message);
        return;
      }

      const existingFileNames = fileAttachmentDetails.map((file) =>
        file.fileName.toLowerCase(),
      );

      // Process and validate files
      const { validFiles, errors } = await processFiles(
        files,
        existingFileNames,
      );

      // Check total file count
      const totalCountValidation = validateTotalFileCount(validFiles);
      if (!totalCountValidation.isValid) {
        showErrorDropzone(totalCountValidation.message);
        return;
      }

      // Update state based on results
      updateFilesState(validFiles, errors);
    },
    [
      selectedAttachmentType,
      setSnack,
      setSelectedFiles,
      selectedFiles,
      MAX_FILE_SIZE,
      MAX_FILE_ALLOWED,
      isFileExtensionAllowed,
      fileAttachmentDetails,
      validateFileContent,
      t,
    ],
  );

  // Helper: Validate attachment type constraints
  const validateAttachmentTypeConstraints = (files) => {
    if (selectedAttachmentType === 6 && files.length > 1) {
      return {
        isValid: false,
        message: t("validate_attachment.message01"),
      };
    }
    return { isValid: true };
  };

  // Helper: Process and validate all files
  const processFiles = async (files, existingFileNames) => {
    let validFiles = [];
    let errors = [];

    for (const file of files) {
      const fileValidation = await validateSingleFile(file, existingFileNames);

      if (!fileValidation.isValid) {
        if (fileValidation.shouldStop) {
          return { validFiles: [], errors, shouldStop: true };
        }
        errors.push(fileValidation.message);
        continue;
      }

      validFiles.push(file);
    }

    return { validFiles, errors };
  };

  // Helper: Validate a single file
  const validateSingleFile = async (file, existingFileNames) => {
    // Check for duplicate file name
    if (existingFileNames.includes(file.name.toLowerCase())) {
      const msg = `${t("validate_attachment.message02")} ${file.name}`;
      setSnack({
        open: true,
        message: msg,
        severity: "error",
      });
      return { isValid: false, message: msg };
    }

    // Validate file content for Attachment Type 6
    if (selectedAttachmentType === 6) {
      const isValid = await validateFileContent(file);
      if (!isValid) {
        return { isValid: false, shouldStop: true };
      }
    }

    // Check file extension
    if (!isFileExtensionAllowed(file.name)) {
      const msg = `${t("validate_attachment.message03")} ${file.name}`;
      setSnack({
        open: true,
        message: msg,
        severity: "error",
      });
      return { isValid: false, message: msg };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      const msg = `${t("validate_attachment.message04")} ${file.name}`;
      setSnack({
        open: true,
        message: msg,
        severity: "error",
      });
      return { isValid: false, message: msg };
    }

    return { isValid: true };
  };

  // Helper: Validate total file count
  const validateTotalFileCount = (validFiles) => {
    const totalFiles = selectedFiles.length + validFiles.length;
    if (totalFiles > MAX_FILE_ALLOWED) {
      return {
        isValid: false,
        message: t("validate_attachment.message05"),
      };
    }
    return { isValid: true };
  };

  // Helper: Update files state based on validation results
  const updateFilesState = (validFiles, errors) => {
    if (errors.length > 0) {
      setDropzoneError(errors.join(" | "));
    } else {
      setDropzoneError("");
    }

    if (selectedAttachmentType === 6) {
      setSelectedFiles(validFiles.slice(0, 1));
    } else {
      setSelectedFiles((prev) => {
        const existingFileNames = prev.map((file) => file.name.toLowerCase());
        const uniqueFiles = validFiles.filter(
          (file) => !existingFileNames.includes(file.name.toLowerCase()),
        );
        return [...prev, ...uniqueFiles];
      });
    }
  };

  // Helper: Show error message and update dropzone error
  const showErrorDropzone = (message) => {
    setSnack({
      open: true,
      message,
      severity: "error",
    });
    setDropzoneError(message);
  };
  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        await validateAndUploadFiles(acceptedFiles);
      }
    },
    [validateAndUploadFiles],
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    multiple: true,
  });
  const handleFileBrowseClick = () => {
    fileInputRef.current.click();
  };
  const handleFileInputChange = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      await validateAndUploadFiles(files);
    }
  };
  const handleOpenDropzone = () => {
    if (!selectedAttachmentType) {
      setSnack({
        open: true,
        message: t("alert.please_select_attachment_type_"),
        severity: "error",
      });
      return;
    }
    setShowDropzone(true);
  };
  const handleRemoveFile = (fileToRemove) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((file) => file !== fileToRemove),
    );
  };
  const handleConfirmationDialog = async () => {
    if (selectedFiles.length === 0) {
      setSnack({
        open: true,
        message: t("alert.please_attach_at_least_one_file_before_uploading_"),
        severity: "error",
      });
    } else if (selectedAttachmentType === 6) {
      const filteredProductionSchedule = fileAttachmentDetails.filter(
        (item) =>
          item.fileAttachmentTypeId === selectedAttachmentType &&
          item.totalUsage === 0,
      );
      if (filteredProductionSchedule.length > 0) {
        const deleteAttachmentDialog = {
          title: t("replace_delete_attachment.title"),
          message: t("replace_delete_attachment.message"),
          messageType: "Confirmation",
          details: [],
          isConfirmation: true,
        };
        handleDialogOpen(
          async () => {
            setLoading(true);
            for (const file of filteredProductionSchedule) {
              try {
                const deleteFileResponse = await deleteFileAttachment(
                  file.fileDocLibID,
                  localStorage.getItem("User"),
                );
                if (!deleteFileResponse.status) {
                  setSnack({
                    open: true,
                    message: `${t("delete_attachment.error_message")} ${
                      file.fileDocLibID
                    }.`,
                    severity: "error",
                  });
                }
              } catch {
                setSnack({
                  open: true,
                  message: t(
                    "alert.error_deleting_one_or_more_existing_production_schedule_files_",
                  ),
                  severity: "error",
                });
              }
            }
            await handleUploadFiles("Replace");
          },
          async () => {
            setLoading(true);
            await handleUploadFiles("Append");
          },
          deleteAttachmentDialog.title,
          deleteAttachmentDialog.message,
          deleteAttachmentDialog.messageType,
          deleteAttachmentDialog.details,
          deleteAttachmentDialog.isConfirmation,
        );
      } else {
        setLoading(true);
        await handleUploadFiles("Append");
      }
    } else {
      setLoading(true);
      await handleUploadFiles("Others");
    }
  };
  const handleUploadFiles = async (uploadMode) => {
    try {
      // Save file attachments
      const savedFiles = await saveFileAttachment(
        classRequestId,
        attachmentMode === "ClassRoster" ? classRosterId : null,
        selectedAttachmentType,
        localStorage.getItem("User"),
        selectedFiles,
        selectedAttachmentType === 6 ? productionScheduleDetails : null,
      );

      if (savedFiles?.status === true) {
        await handleSuccessfulUpload(uploadMode);
      } else {
        showError(t("snack_error_message02"));
      }
    } catch {
      showError(t("alert.error_uploading_files_please_try_again_"));
    } finally {
      setLoading(false);
    }
  };

  // Helper: Handle successful file upload
  const handleSuccessfulUpload = async (uploadMode) => {
    const lookupUniqueId =
      attachmentMode === "ClassRequest" ? classRequestId : classRosterId;

    const fileAttachmentDetailsResponse = await getFileAttachmentDetails(
      lookupUniqueId,
      attachmentMode,
    );

    if (fileAttachmentDetailsResponse?.message === "Success") {
      updateFileAttachmentState(fileAttachmentDetailsResponse.data || []);
      clearUploadState();
      showSuccessMessage(uploadMode);
    } else {
      showError(t("snack_error_message01"));
    }
  };

  // Helper: Update file attachment state after successful fetch
  const updateFileAttachmentState = (updatedFileAttachmentDetails) => {
    setFileAttachmentDetails(updatedFileAttachmentDetails);

    // Update filtered attachment details
    setFilteredAttachmentDetails(
      selectedAttachmentType === 0
        ? updatedFileAttachmentDetails
        : updatedFileAttachmentDetails.filter(
            (item) => item.fileAttachmentTypeId === selectedAttachmentType,
          ),
    );

    // Update file counts for attachment types
    const fileCounts = updatedFileAttachmentDetails.reduce((acc, item) => {
      acc[item.fileAttachmentTypeId] =
        (acc[item.fileAttachmentTypeId] || 0) + 1;
      return acc;
    }, {});

    const updatedAttachmentTypes = attachmentTypes.map((type) => ({
      ...type,
      totalFiles: fileCounts[type.fileAttachmentTypeId] || 0,
    }));

    setAttachmentTypes(updatedAttachmentTypes);
  };

  // Helper: Clear upload-related state
  const clearUploadState = () => {
    setSelectedFiles([]);
    setShowDropzone(false);
    setDropzoneError("");
  };

  // Helper: Get success message based on upload mode
  const getSuccessMessage = (uploadMode) => {
    switch (uploadMode) {
      case "Append":
        return `${t("file_upload_success.message01")} ${
          productionScheduleDetails.length
        }`;
      case "Replace":
        return `${t("file_upload_success.message02")} ${
          productionScheduleDetails.length
        }.`;
      default:
        return t("file_upload_success.message03");
    }
  };

  // Helper: Show success message
  const showSuccessMessage = (uploadMode) => {
    setSnack({
      open: true,
      message: getSuccessMessage(uploadMode),
      severity: "success",
    });
  };

  // Helper: Show error message
  const showError = (message) => {
    setSnack({
      open: true,
      message,
      severity: "error",
    });
  };
  const handleDownloadSampleFile = () => {
    const filePath =
      process.env.PUBLIC_URL + "/assets/files/ProductionScheduleTemplate.xlsx";
    const link = document.createElement("a");
    link.href = filePath;
    link.setAttribute("download", "ProductionScheduleTemplate.xlsx");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const getNoFilesMessage = () => {
    if (selectedAttachmentType !== 0) {
      return t("no_file_selected.message01");
    } else {
      return `${t("no_file_selected.message02")} ${
        attachmentMode === "ClassRequest"
          ? t("no_file_selected.classRequest")
          : t("no_file_selected.classRoster")
      }.`;
    }
  };
  const attachmentTypeOptions = useMemo(() => {
    return [
      {
        fileAttachmentTypeId: 0,
        fileAttachmentType: "All Types",
        totalFiles: fileAttachmentDetails.length,
      },
      ...attachmentTypes,
    ];
  }, [attachmentTypes, fileAttachmentDetails]);
  return (
    <>
      <Portal>
        <DialogBox
          isOpen={dialogOpen}
          action={dialogConfirmAction}
          cancelAction={dialogCancelAction}
          title={dialogTitle}
          message={dialogMessage}
          messageType={dialogMessageType || "Confirmation"}
          itemList={dialogBulletList}
          isConfirmation={isConfirmationDialog}
          sx={{
            zIndex: 1700,
          }}
        />
      </Portal>

      <Dialog
        open={attachmentDialogOpen}
        onClose={handleAttachmentDialogClose}
        scroll="paper"
        TransitionComponent={Transition}
        fullWidth
        maxWidth="md"
        slotProps={{
          backdrop: {
            style: {
              zIndex: (theme) => theme.zIndex.modal + 1,
            },
          },
        }}
      >
        <DialogTitle>
          <MainHeading>
            {attachmentMode === "ClassRequest"
              ? t("mainheading.class_request_attachment")
              : t("mainheading.class_roster_attachment")}
          </MainHeading>
          <IconButton
            aria-label={t("aria-label.close")}
            onClick={handleAttachmentDialogClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers={true}
          sx={{
            minHeight: {
              xs: "300px",
              sm: "400px",
              md: "500px",
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <SubHeading>{t("subheading.attachment_type")}</SubHeading>
              <AutocompleteWrapper>
                <Autocomplete
                  freeSolo
                  disablePortal
                  blurOnSelect
                  size="small"
                  disableClearable
                  disableCloseOnSelect
                  options={attachmentTypeOptions}
                  getOptionLabel={(option) => option.fileAttachmentType}
                  value={
                    attachmentTypeOptions.find(
                      (option) =>
                        option.fileAttachmentTypeId === selectedAttachmentType,
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleValueChange(event, newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t("placeholder.select_type")}
                      variant="outlined"
                      size="small"
                    />
                  )}
                  renderOption={(props, option) => (
                    <ListItem
                      {...props}
                      key={option.fileAttachmentTypeId}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <ListItemText primary={option.fileAttachmentType} />
                      {option.totalFiles > 0 && (
                        <Chip
                          label={option.totalFiles}
                          size="small"
                          sx={{
                            backgroundColor: "#3299FF",
                            color: "white",
                          }}
                        />
                      )}
                    </ListItem>
                  )}
                />
              </AutocompleteWrapper>
            </Grid>
            <Grid
              item
              xs={4}
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-start",
                paddingBottom: 0.25,
                gap: 1,
              }}
            >
              {selectedAttachmentType === 6 && (
                <Tooltip
                  title={
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "1rem",
                      }}
                    >
                      {t("typography.sample_file")}
                    </Typography>
                  }
                  arrow
                >
                  <IconButton
                    onClick={handleDownloadSampleFile}
                    color="primary"
                    sx={{
                      backgroundColor: "#E0F2FF",
                      "&:hover": {
                        backgroundColor: "#D1E9FF",
                      },
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              )}
              <CustomButton
                variant="contained"
                onClick={handleOpenDropzone}
                startIcon={<AttachFileIcon />}
                disabled={selectedAttachmentType === 0}
              >
                {t("custombutton.attach_files")}
              </CustomButton>
            </Grid>
            {showDropzone && (
              <Grid item xs={12}>
                <Stack spacing={2}>
                  {selectedFiles.length > 0 && (
                    <>
                      <SubHeading
                        sx={{
                          paddingBottom: 0,
                          margin: 0,
                        }}
                      >
                        {t("subheading.attached_files")}
                      </SubHeading>
                      <Box
                        display="flex"
                        flexWrap="wrap"
                        alignItems="flex-start"
                        justifyContent="flex-start"
                        gap={1}
                      >
                        {selectedFiles.map((file) => (
                          <Chip
                            key={file.name}
                            label={file.name}
                            onDelete={() => handleRemoveFile(file)}
                            deleteIcon={
                              <CloseIcon
                                sx={{
                                  color: "#3299FF",
                                }}
                              />
                            }
                            sx={{
                              fontFamily: "Urbanist, sans-serif",
                              letterSpacing: "0.15em",
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              backgroundColor: "#ffffff",
                              color: "#1e1e1e",
                              fontWeight: 600,
                              border: "1px solid #cfd8dc",
                              paddingY: 0.5,
                              justifyContent: "flex-start",
                              "& .MuiChip-label": {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "block",
                                textAlign: "left",
                                width: "100%",
                              },
                              "&:hover": {
                                backgroundColor: "#f5f5f5",
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </>
                  )}

                  <Paper
                    {...getRootProps()}
                    elevation={3}
                    sx={{
                      position: "relative",
                      width: "100%",
                      p: 4,
                      textAlign: "center",
                      border: "2px dashed",
                      borderColor: isDragActive ? "primary.main" : "grey.400",
                      backgroundColor: isDragActive ? "grey.100" : "white",
                      cursor: "pointer",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedFiles([]);
                        setShowDropzone(false);
                        setDropzoneError("");
                      }}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "grey.600",
                      }}
                    >
                      <CloseIcon />
                    </IconButton>

                    <input {...getInputProps()} />

                    <CustomTypography variant="h6">
                      {isDragActive
                        ? t("drawer.message02")
                        : t("drawer.message01")}
                    </CustomTypography>

                    <CustomTypography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        mt: 1,
                      }}
                    >
                      or
                    </CustomTypography>

                    <CustomButton
                      variant="contained"
                      onClick={handleFileBrowseClick}
                      sx={{
                        mt: 2,
                      }}
                      startIcon={<OpenInBrowserIcon />}
                    >
                      {t("custombutton.browse_files")}
                    </CustomButton>

                    {selectedFiles.length > 0 && (
                      <CustomButton
                        variant="contained"
                        onClick={handleConfirmationDialog}
                        sx={{
                          mt: 2,
                          ml: 1,
                        }}
                        startIcon={<CloudDoneIcon />}
                      >
                        {t("custombutton.upload_files")}
                      </CustomButton>
                    )}

                    {dropzoneError && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 2,
                          color: "error.main",
                          fontFamily: "Urbanist, sans-serif",
                          textAlign: "center",
                        }}
                      >
                        {dropzoneError}
                      </Typography>
                    )}

                    <Box
                      component="input"
                      type="file"
                      multiple={selectedAttachmentType !== 6}
                      ref={fileInputRef}
                      onChange={handleFileInputChange}
                      sx={{ display: "none" }}
                    />
                  </Paper>
                </Stack>
              </Grid>
            )}
            {filteredAttachmentDetails.length > 0 ? (
              <Grid item xs={12}>
                <PrimeReactDataGrid
                  dataKey="fileDocLibID"
                  size="normal"
                  removableSort
                  resizableColumns
                  showGridlines
                  paginator={filteredAttachmentDetails.length > 5}
                  rows={5}
                  paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                  currentPageReportTemplate={t(
                    "pagination_template.showing_first_to_last_of_totalrecords_record_s_",
                  )}
                  columnResizeMode="expand"
                  value={filteredAttachmentDetails}
                  columns={fileAttachmentColumns}
                  metaKeySelection={false}
                  enableGlobalFilter={false}
                  enableStickyHeader={true}
                  enableScrollable={false}
                  sortMode="multiple"
                  emptyMessage={t("empty_message.no_records_found_")}
                ></PrimeReactDataGrid>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h7"
                    sx={{
                      color: "#3F5765",
                      fontWeight: "bold",
                      letterSpacing: "0.10em",
                      padding: 2,
                    }}
                  >
                    {getNoFilesMessage()}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
}
FileAttachment.propTypes = {
  attachmentMode: PropTypes.string.isRequired,
  setLoading: PropTypes.func.isRequired,
  setSnack: PropTypes.func.isRequired,
  classRequestId: PropTypes.number,
  classRosterId: PropTypes.number,
  attachmentDialogOpen: PropTypes.bool.isRequired,
  handleAttachmentDialogClose: PropTypes.func.isRequired,
};
